import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { readResponse } from '../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALL_ROLES = ['Contractor', 'Estimator', 'Labor', 'Marketing', 'Office', 'Production', 'Project Lead', 'Time Approver'];
const ALL_FRANCHISE = [
  'Langhorne/Bensalem (10089)',
  'Fox Chase/Torresdale (10090)',
  'Society Hill (11669)',
  'Burlington Township/Mount Holly (11670)',
  'Downtown Philadelphia (19032)'
];
const ALL_APP_ACCESS = ['Create Records', 'Update Records', 'Delete Records', 'Setup Options', 'Management Options', 'Update Customer IDs'];

const EditUserProfile = () => {
  const { email } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [hasChanges, setHasChanges] = useState(false);
  const initialData = useRef(null);

  const [inactive, setInactive] = useState(false);
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [payRate, setPayRate] = useState('');
  const [bonusPct, setBonusPct] = useState('');
  const [roles, setRoles] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [franchiseAccess, setFranchiseAccess] = useState([]);
  const [appAccess, setAppAccess] = useState([]);
  const [commissionManagement, setCommissionManagement] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // Permission lock-down: only admins or users with Management Options
  // permission can change the Application Access checkboxes. Non-mgmt
  // users still see them (read-only) so they can see what their own
  // permissions are.
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } })();
  const permsLocked = !(currentUser?.is_admin || currentUser?.permissions?.management);
  // The admin checkbox is stricter: only an existing admin can toggle it,
  // and you can't strip your own admin (prevents lockout). The login
  // identifier ("mike") may differ from the canonical employee email
  // ("mpaparone@..."), so we match on canonical_email when available.
  const myCanonical = (currentUser?.canonical_email || currentUser?.email || '').toLowerCase();
  const isEditingSelf = myCanonical === (email || '').toLowerCase();
  const adminToggleLocked = !currentUser?.is_admin || isEditingSelf;
  const [welcomeStatus, setWelcomeStatus] = useState(null);

  useEffect(() => {
    fetchEmployee();
    fetchWelcomeStatus();
  }, [email]);

  const fetchWelcomeStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/employees/${encodeURIComponent(email)}/welcome-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return;
      const { data } = await readResponse(res);
      setWelcomeStatus(data);
    } catch {
      // silent — badge just won't show
    }
  };

  const fetchEmployee = async () => {
    try {
      const res = await fetch(`${API_URL}/api/employees/${encodeURIComponent(email)}`);
      if (!res.ok) throw new Error('Not found');
      const { data } = await readResponse(res);

      setInactive(!data.active);
      setLastName(data.last_name || '');
      setFirstName(data.first_name || '');
      setUserEmail(data.email || '');
      setPayRate(data.pay_rate != null ? String(data.pay_rate) : '0');
      setBonusPct(data.bonus_pct != null ? String(data.bonus_pct) : '0');
      setRoles(data.roles || []);
      setCertifications(data.certifications || []);
      setFranchiseAccess(data.franchise_access || ALL_FRANCHISE);
      setCommissionManagement(data.commission_management || false);
      setIsAdmin(!!data.is_admin);

      // Derive app access from fields
      const access = [];
      if (data.can_create === 'Yes') access.push('Create Records');
      if (data.can_update === 'Yes') access.push('Update Records');
      if (data.can_delete === 'Yes') access.push('Delete Records');
      if (data.setup === 'Yes') access.push('Setup Options');
      if (data.management === 'Yes') access.push('Management Options');
      if (data.update_customer_ids === 'Yes') access.push('Update Customer IDs');
      setAppAccess(access);

      initialData.current = {
        inactive: !data.active, lastName: data.last_name || '', firstName: data.first_name || '',
        userEmail: data.email || '', payRate: data.pay_rate != null ? String(data.pay_rate) : '0',
        bonusPct: data.bonus_pct != null ? String(data.bonus_pct) : '0',
        roles: JSON.stringify(data.roles || []), certifications: JSON.stringify(data.certifications || []),
        franchiseAccess: JSON.stringify(data.franchise_access || ALL_FRANCHISE),
        appAccess: JSON.stringify(access), commissionManagement: data.commission_management || false,
        isAdmin: !!data.is_admin
      };
    } catch {
      toast.error('Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!initialData.current) return;
    const d = initialData.current;
    const changed = inactive !== d.inactive || lastName !== d.lastName || firstName !== d.firstName ||
      userEmail !== d.userEmail || payRate !== d.payRate || bonusPct !== d.bonusPct ||
      JSON.stringify(roles) !== d.roles || JSON.stringify(certifications) !== d.certifications ||
      JSON.stringify(franchiseAccess) !== d.franchiseAccess || JSON.stringify(appAccess) !== d.appAccess ||
      commissionManagement !== d.commissionManagement || isAdmin !== d.isAdmin;
    setHasChanges(changed);
  }, [inactive, lastName, firstName, userEmail, payRate, bonusPct, roles, certifications, franchiseAccess, appAccess, commissionManagement, isAdmin]);

  const toggleItem = (list, setList, item) => {
    setList(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
  };

  const handleSave = async () => {
    // Re-entrancy guard against rapid double-click.
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const body = {
        last_name: lastName,
        first_name: firstName,
        email: userEmail,
        active: !inactive,
        pay_rate: parseFloat(payRate) || 0,
        bonus_pct: parseFloat(bonusPct) || 0,
        roles,
        certifications,
        franchise_access: franchiseAccess,
        can_create: appAccess.includes('Create Records') ? 'Yes' : 'No',
        can_update: appAccess.includes('Update Records') ? 'Yes' : 'No',
        can_delete: appAccess.includes('Delete Records') ? 'Yes' : 'No',
        setup: appAccess.includes('Setup Options') ? 'Yes' : 'No',
        management: appAccess.includes('Management Options') ? 'Yes' : 'No',
        update_customer_ids: appAccess.includes('Update Customer IDs') ? 'Yes' : 'No',
        commission_management: commissionManagement
      };
      // Only send is_admin when the caller is an admin AND the value
      // actually changed — backend rejects this field from non-admins.
      if (currentUser?.is_admin && initialData.current && isAdmin !== initialData.current.isAdmin) {
        body.is_admin = isAdmin;
      }

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/employees/${encodeURIComponent(email)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('User saved successfully');
      initialData.current = {
        inactive, lastName, firstName, userEmail, payRate, bonusPct,
        roles: JSON.stringify(roles), certifications: JSON.stringify(certifications),
        franchiseAccess: JSON.stringify(franchiseAccess), appAccess: JSON.stringify(appAccess),
        commissionManagement, isAdmin
      };
      setHasChanges(false);
    } catch {
      toast.error('Failed to save user');
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  if (loading) {
    return <Layout><div className="p-8 text-center text-gray-500">Loading user...</div></Layout>;
  }

  const handleResendWelcome = async () => {
    if (!userEmail) {
      toast.error('This user has no email address on file');
      return;
    }
    if (!window.confirm(`Resend welcome / password-setup email to ${userEmail}? Any previous setup link for this user will be invalidated.`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/employees/${encodeURIComponent(email)}/resend-welcome`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const { data } = await readResponse(res);
      if (!res.ok) {
        toast.error(data.detail || 'Failed to resend welcome email');
        return;
      }
      toast.success(`Welcome email re-sent to ${userEmail}`);
      fetchWelcomeStatus();
    } catch (err) {
      toast.error(`Failed to resend welcome email: ${err.message}`);
    }
  };

  const TONE_CLASSES = {
    green: 'bg-green-100 text-green-700 border-green-300',
    blue: 'bg-blue-100 text-blue-700 border-blue-300',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    red: 'bg-red-100 text-red-700 border-red-300',
    gray: 'bg-gray-100 text-gray-600 border-gray-300',
  };

  const formatSentAt = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch {
      return null;
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8">
        <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-lg text-[#5B8DB8] font-light">Setup</h2>
            <h1 className="text-3xl text-gray-700 font-light italic">Edit User Profile</h1>
          </div>
          {welcomeStatus && (
            <div
              className="flex flex-col items-end gap-1"
              data-testid="welcome-email-status"
            >
              <span className="text-xs text-gray-500 uppercase tracking-wide">Welcome Email</span>
              <span
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm font-medium ${TONE_CLASSES[welcomeStatus.tone] || TONE_CLASSES.gray}`}
                data-testid="welcome-email-badge"
                title={welcomeStatus.error || (welcomeStatus.sent_at ? `Last attempt: ${formatSentAt(welcomeStatus.sent_at)}` : '')}
              >
                <span className={`w-2 h-2 rounded-full ${
                  welcomeStatus.tone === 'green' ? 'bg-green-500'
                  : welcomeStatus.tone === 'blue' ? 'bg-blue-500'
                  : welcomeStatus.tone === 'yellow' ? 'bg-yellow-500'
                  : welcomeStatus.tone === 'red' ? 'bg-red-500'
                  : 'bg-gray-400'
                }`}></span>
                {welcomeStatus.label}
              </span>
              {welcomeStatus.sent_at && welcomeStatus.status !== 'not_sent' && (
                <span className="text-xs text-gray-400">Last sent: {formatSentAt(welcomeStatus.sent_at)}</span>
              )}
              {welcomeStatus.error && (
                <span className="text-xs text-red-600 max-w-xs text-right">{welcomeStatus.error}</span>
              )}
            </div>
          )}
        </div>

        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={() => navigate('/setup/users-employees')}
            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded transition-colors"
            data-testid="back-btn"
          >
            Back
          </button>
          <button
            onClick={handleResendWelcome}
            className="bg-[#4ECFDB] hover:bg-[#3DB8C4] text-white px-6 py-2 rounded transition-colors"
            data-testid="resend-welcome-btn"
          >
            Resend Welcome Email
          </button>
        </div>

        <div className="bg-white rounded shadow p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {/* User Information */}
            <div>
              <h2 className="text-xl text-gray-700 font-semibold italic mb-6">User Information</h2>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <label className="text-gray-700 text-sm w-24 text-left">Inactive:</label>
                  <input
                    type="checkbox"
                    checked={inactive}
                    onChange={(e) => setInactive(e.target.checked)}
                    className="w-4 h-4"
                    data-testid="inactive-checkbox"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-red-500 text-sm w-24 text-right">Last Name:</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 flex-1"
                    data-testid="last-name-input"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-red-500 text-sm w-24 text-right">First Name:</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 flex-1"
                    data-testid="first-name-input"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-red-500 text-sm w-24 text-right">Email:</label>
                  <input
                    type="email"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 flex-1"
                    data-testid="email-input"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm w-24 text-right">Pay Rate:</label>
                  <input
                    type="number"
                    value={payRate}
                    onChange={(e) => setPayRate(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 w-20"
                    data-testid="pay-rate-input"
                  />
                  <span className="text-sm text-gray-500">per hr</span>
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm w-24 text-right">Bonus %:</label>
                  <input
                    type="number"
                    value={bonusPct}
                    onChange={(e) => setBonusPct(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 w-20"
                    data-testid="bonus-pct-input"
                  />
                  <span className="text-sm text-gray-500">% (0-100)</span>
                </div>
                <p className="text-xs text-gray-400 ml-28">Percent of value minus expenses and commission</p>
              </div>
            </div>

            {/* Roles */}
            <div>
              <h2 className="text-xl text-gray-700 font-semibold italic mb-6">Roles</h2>
              <div className="space-y-3">
                {ALL_ROLES.map(role => (
                  <label key={role} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={roles.includes(role)}
                      onChange={() => toggleItem(roles, setRoles, role)}
                      className="w-4 h-4 accent-[#3B82F6]"
                    />
                    {role}
                  </label>
                ))}
              </div>
            </div>

            {/* Certifications */}
            <div>
              <h2 className="text-xl text-gray-700 font-semibold italic mb-6">Certifications</h2>
              <p className="text-sm text-gray-400 italic">No certifications configured</p>
            </div>

            {/* Franchise Access */}
            <div>
              <h2 className="text-xl text-gray-700 font-semibold italic mb-6">Franchise Access</h2>
              <div className="space-y-3">
                {ALL_FRANCHISE.map(loc => (
                  <label key={loc} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={franchiseAccess.includes(loc)}
                      onChange={() => toggleItem(franchiseAccess, setFranchiseAccess, loc)}
                      className="w-4 h-4 accent-[#3B82F6]"
                    />
                    {loc}
                  </label>
                ))}
              </div>
            </div>

            {/* Application Access */}
            <div>
              <h2 className="text-xl text-gray-700 font-semibold italic mb-2">Application Access</h2>
              {permsLocked && (
                <div className="text-xs text-gray-500 italic mb-4" data-testid="perms-locked-notice">
                  Read-only. Only admins or users with the Management Options permission can change these.
                </div>
              )}
              <div className={`space-y-3 ${permsLocked ? 'opacity-70' : ''}`} data-testid="app-access-section">
                {ALL_APP_ACCESS.map(access => (
                  <label key={access} className={`flex items-center gap-2 text-sm text-gray-700 ${permsLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={appAccess.includes(access)}
                      onChange={() => toggleItem(appAccess, setAppAccess, access)}
                      disabled={permsLocked}
                      className="w-4 h-4 accent-[#3B82F6] disabled:cursor-not-allowed"
                      data-testid={`perm-${access.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                    />
                    {access}
                  </label>
                ))}
                <label className={`flex items-center gap-2 text-sm text-gray-700 ${permsLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={commissionManagement}
                    onChange={(e) => setCommissionManagement(e.target.checked)}
                    disabled={permsLocked}
                    className="w-4 h-4 accent-[#3B82F6] disabled:cursor-not-allowed"
                    data-testid="perm-commission-management"
                  />
                  Commission Management
                </label>
                <label
                  className={`flex items-center gap-2 text-sm pt-3 mt-1 border-t border-gray-100 ${adminToggleLocked ? 'text-gray-500 cursor-not-allowed' : 'text-gray-700 cursor-pointer'}`}
                  title={
                    isEditingSelf
                      ? "You can't change your own admin flag."
                      : !currentUser?.is_admin
                        ? "Only an existing admin can grant or revoke the admin flag."
                        : "Grants full admin access: all Setup admin tools, Project History, the bidirectional customer cascade, and admin-only delete buttons."
                  }
                >
                  <input
                    type="checkbox"
                    checked={isAdmin}
                    onChange={(e) => setIsAdmin(e.target.checked)}
                    disabled={adminToggleLocked}
                    className="w-4 h-4 accent-[#FFA94D] disabled:cursor-not-allowed"
                    data-testid="perm-admin"
                  />
                  <span className="font-medium">Admin</span>
                  <span className="text-xs text-gray-400 italic ml-1">
                    {isEditingSelf ? '(self — locked)' : 'unlocks all admin tools & cascades'}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="mt-8">
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className={`px-6 py-2 rounded transition-colors border disabled:opacity-50 ${hasChanges ? 'bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white border-[#4ECFDB]' : 'bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white border-[#4ECFDB]'}`}
              data-testid="save-user-btn"
            >
              {saving ? 'Saving...' : 'Save User'}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EditUserProfile;
